import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Trash2, Mail, MessageSquare, Sparkles, Send as SendIcon, AlertCircle, Plus, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUserCustomers, deleteCustomer } from '../../services/firestore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomersPage = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('recent'); // 'recent' or 'oldest'
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [smsContent, setSmsContent] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);

  const maskEmail = (email) => {
    if (!email) return '-';
    const [name, domain] = email.split('@');
    if (name.length <= 3) return `${name[0]}***@${domain}`;
    return `${name.substring(0, 3)}***@${domain}`;
  };

  const maskPhone = (phone) => {
    if (!phone) return '-';
    if (phone.length <= 4) return '****';
    return `${phone.substring(0, phone.length - 4)}****`;
  };

  useEffect(() => {
    if (currentUser) {
      fetchCustomers();
    }
  }, [currentUser]);

  const fetchCustomers = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const data = await getUserCustomers(currentUser.uid);
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client?')) return;
    
    try {
      await deleteCustomer(customerId);
      setCustomers(customers.filter(c => c.id !== customerId));
      toast.success('Client supprimé avec succès');
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectCustomer = (customerId, checked) => {
    if (checked) {
      setSelectedCustomers([...selectedCustomers, customerId]);
    } else {
      setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
    }
  };

  const handleGenerateEmailAI = async () => {
    if (!currentUser) return;
    setGeneratingAI(true);
    
    const targetCount = selectedCustomers.length > 0 ? selectedCustomers.length : customers.length;
    
    try {
      const response = await axios.post(`${API}/marketing/generate-email`, {
        user_id: currentUser.uid,
        customer_count: targetCount
      });
      
      setEmailSubject(response.data.subject);
      setEmailContent(response.data.content);
      toast.success('Email généré par IA!');
    } catch (error) {
      console.error('Error generating email:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleGenerateSmsAI = async () => {
    if (!currentUser) return;
    setGeneratingAI(true);
    
    const targetCount = selectedCustomers.length > 0 ? selectedCustomers.length : customers.length;
    
    try {
      const response = await axios.post(`${API}/marketing/generate-sms`, {
        user_id: currentUser.uid,
        customer_count: targetCount
      });
      
      setSmsContent(response.data.content);
      toast.success('SMS généré par IA!');
    } catch (error) {
      console.error('Error generating SMS:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailContent) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    // Si aucun client sélectionné, envoyer à tous
    const targetCustomerIds = selectedCustomers.length > 0 
      ? selectedCustomers 
      : customers.map(c => c.id);

    if (targetCustomerIds.length === 0) {
      toast.error('Aucun client disponible');
      return;
    }

    // Récupérer les données complètes des clients sélectionnés
    const customersData = customers.filter(c => targetCustomerIds.includes(c.id)).map(c => ({
      id: c.id,
      email: c.email,
      name: `${c.first_name} ${c.last_name}`.trim() || 'Client'
    }));

    try {
      const response = await axios.post(`${API}/marketing/send-email`, {
        user_id: currentUser.uid,
        customers_data: customersData,
        subject: emailSubject,
        content: emailContent,
        business_name: userProfile?.business_name || 'Markeap'
      });
      
      const message = selectedCustomers.length > 0
        ? `Email envoyé à ${response.data.sent_count} client(s) sélectionné(s)!`
        : `Email envoyé à ${response.data.sent_count} client(s)!`;
      
      if (response.data.failed_count > 0) {
        toast.warning(`${message} (${response.data.failed_count} échecs)`);
      } else {
        toast.success(message);
      }
      
      // Show remaining credits
      if (response.data.email_credits_remaining !== null && response.data.email_credits_remaining !== undefined) {
        toast.info(`Crédits Email restants: ${response.data.email_credits_remaining}`, {
          duration: 3000
        });
      }
      
      setShowEmailModal(false);
      setEmailSubject('');
      setEmailContent('');
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Error sending email:', error);
      if (error.response?.status === 402) {
        toast.error('Crédits Email insuffisants! Achetez plus de crédits dans les Paramètres.');
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    }
  };

  const handleSendSms = async () => {
    if (!smsContent) {
      toast.error('Veuillez remplir le message SMS');
      return;
    }

    // Si aucun client sélectionné, envoyer à tous
    const targetCustomerIds = selectedCustomers.length > 0 
      ? selectedCustomers 
      : customers.map(c => c.id);

    if (targetCustomerIds.length === 0) {
      toast.error('Aucun client disponible');
      return;
    }

    // Récupérer les données complètes des clients sélectionnés
    const customersData = customers.filter(c => targetCustomerIds.includes(c.id)).map(c => ({
      id: c.id,
      phone: c.phone,
      first_name: c.first_name,
      last_name: c.last_name
    }));

    try {
      const response = await axios.post(`${API}/marketing/send-sms`, {
        user_id: currentUser.uid,
        customers_data: customersData,
        content: smsContent
      });
      
      const message = selectedCustomers.length > 0
        ? `SMS envoyé à ${response.data.sent_count} client(s) sélectionné(s)!`
        : `SMS envoyé à ${response.data.sent_count} client(s)!`;
      
      if (response.data.failed_count > 0) {
        toast.warning(`${message} (${response.data.failed_count} échecs)`);
      } else {
        toast.success(message);
      }
      
      // Show remaining credits
      if (response.data.sms_credits_remaining !== null && response.data.sms_credits_remaining !== undefined) {
        toast.info(`Crédits SMS restants: ${response.data.sms_credits_remaining}`, {
          duration: 3000
        });
      }
      
      setShowSmsModal(false);
      setSmsContent('');
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Error sending SMS:', error);
      if (error.response?.status === 402) {
        toast.error('Crédits SMS insuffisants! Achetez plus de crédits dans les Paramètres.');
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    }
  };

  const filteredCustomers = customers
    .filter(customer =>
      customer.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const dateA = new Date(a.date_added || 0);
      const dateB = new Date(b.date_added || 0);
      return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Base de Données Clients</h2>
        <p className="text-gray-600">Gérez vos clients et envoyez des promotions</p>
      </div>

      {/* Stats Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Customers Card */}
        <Card className="p-6 bg-gradient-to-r from-[#2E59D7] to-[#0082FB] text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Total Clients</p>
              <p className="text-5xl font-bold">{customers.length}</p>
            </div>
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </Card>

        {/* SMS Credits Card */}
        <Card className="p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Crédits SMS</p>
              <p className="text-5xl font-bold">
                {userProfile?.sms_credits !== undefined ? userProfile.sms_credits : '30'}
              </p>
            </div>
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
          </div>
        </Card>

        {/* Email Credits Card */}
        <Card className="p-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Crédits Email</p>
              <p className="text-5xl font-bold">
                {userProfile?.email_credits !== undefined ? userProfile.email_credits : '30'}
              </p>
            </div>
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      {/* Low Credits Warning */}
      {(userProfile?.sms_credits < 5 || userProfile?.email_credits < 5) && (
        <Card className="border-2 border-orange-300 bg-orange-50">
          <div className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-orange-900 mb-1">
                ⚠️ Crédits faibles
              </p>
              <p className="text-sm text-orange-800 mb-3">
                {userProfile?.sms_credits < 5 && userProfile?.email_credits < 5 
                  ? "Vos crédits SMS et Email sont presque épuisés."
                  : userProfile?.sms_credits < 5 
                    ? "Vos crédits SMS sont presque épuisés."
                    : "Vos crédits Email sont presque épuisés."
                }
                {' '}Rechargez maintenant pour continuer vos campagnes.
              </p>
              <Button 
                size="sm"
                className="bg-orange-600 hover:bg-orange-700"
                onClick={() => navigate('/dashboard/settings')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Acheter des crédits
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Search & Actions */}
      <Card className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex gap-3 flex-1 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Rechercher des clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              {/* Sort Filter */}
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2E59D7] focus:border-transparent cursor-pointer"
                data-testid="sort-select"
              >
                <option value="recent">Plus récent</option>
                <option value="oldest">Plus ancien</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowEmailModal(true)}
                className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]"
              >
                <Mail className="w-4 h-4 mr-2" />
                Envoyer Email
              </Button>
              <Button
                onClick={() => setShowSmsModal(true)}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Envoyer SMS
              </Button>
            </div>
          </div>
          
          {selectedCustomers.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-semibold text-[#2E59D7]">
                ✓ {selectedCustomers.length} client(s) sélectionné(s)
              </span>
              <span className="text-sm text-gray-600">
                (Les messages seront envoyés uniquement à ces clients)
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Customer Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Prix Gagné</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-12">
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedCustomers.includes(customer.id)}
                        onCheckedChange={(checked) => handleSelectCustomer(customer.id, checked)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {customer.first_name} {customer.last_name}
                    </TableCell>
                    <TableCell>{maskEmail(customer.email)}</TableCell>
                    <TableCell>{maskPhone(customer.phone)}</TableCell>
                    <TableCell>
                      {customer.prize_won ? (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                          {customer.prize_won}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.date_added ? 
                        new Date(customer.date_added).toLocaleDateString('fr-FR') 
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(customer.id)}
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#2E59D7]" />
              Envoyer un Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="email-subject">Objet de l'email *</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Ex: Offre spéciale pour nos clients fidèles"
                className="mt-2"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="email-content">Contenu de l'email *</Label>
                <Button
                  onClick={handleGenerateEmailAI}
                  disabled={generatingAI}
                  variant="outline"
                  size="sm"
                  className="text-purple-600 hover:text-purple-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {generatingAI ? 'Génération...' : 'Générer avec IA'}
                </Button>
              </div>
              <Textarea
                id="email-content"
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                placeholder="Saisissez le contenu de votre email..."
                rows={8}
                className="mt-1"
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700">
                {selectedCustomers.length > 0 ? (
                  <>
                    <strong>{selectedCustomers.length}</strong> client(s) sélectionné(s) recevront cet email
                  </>
                ) : (
                  <>
                    <strong>Tous les clients ({customers.length})</strong> recevront cet email
                  </>
                )}
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowEmailModal(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSendEmail}
                className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB]"
              >
                <SendIcon className="w-4 h-4 mr-2" />
                Envoyer Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-green-600" />
              Envoyer un SMS
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="sms-content">Message SMS *</Label>
                <Button
                  onClick={handleGenerateSmsAI}
                  disabled={generatingAI}
                  variant="outline"
                  size="sm"
                  className="text-purple-600 hover:text-purple-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {generatingAI ? 'Génération...' : 'Générer avec IA'}
                </Button>
              </div>
              <Textarea
                id="sms-content"
                value={smsContent}
                onChange={(e) => setSmsContent(e.target.value)}
                placeholder="Saisissez votre message SMS (max 160 caractères)..."
                rows={4}
                maxLength={160}
                className="mt-1"
              />
              <p className="text-sm text-gray-500 mt-1">
                {smsContent.length} / 160 caractères
              </p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700">
                {selectedCustomers.length > 0 ? (
                  <>
                    <strong>{selectedCustomers.length}</strong> client(s) sélectionné(s) recevront ce SMS
                  </>
                ) : (
                  <>
                    <strong>Tous les clients ({customers.length})</strong> recevront ce SMS
                  </>
                )}
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowSmsModal(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSendSms}
                className="bg-gradient-to-r from-green-500 to-green-600"
              >
                <SendIcon className="w-4 h-4 mr-2" />
                Envoyer SMS
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;